"use client";

import Image from "next/image";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import { AppButton } from "@/components/ui/Button";
import ProfessionalMatchBanner from "../../_components/global/ProfessionalMatchBanner";
import { useCityPopup } from "../../_provider/CityPopupProvider";
import { inhomzEvents } from "../../_lib/events";
import { useInhomzTrack } from "../../_hooks/useInhomzTrack";

const BROWSE_BUTTON_SX = {
  backgroundColor: "var(--colorSurfaceSurface)",
  color: "var(--colorPrimaryPrimary)",
  borderRadius: "var(--radius100)",
  fontWeight: 600,
  paddingX: "24px",
  paddingY: "12px",
  "&:hover": {
    backgroundColor: "var(--colorSurfaceSurface)",
    opacity: 0.9,
  },
};

// `variant="banner"` (default) is the full-width banner — used standalone on
// the design-ideas landing page, and reused as-is for the design-idea detail
// page's own mobile/tablet "no city yet" slot (DesignIdeaPageLayout wraps it
// in its own responsive-visibility div there, so this component doesn't
// manage any lg:hidden/hidden:lg itself — a caller-placed instance is either
// fully visible or not rendered at all, never responsively half-hidden).
// `variant="compact-card"` is the desktop-only version of the same CTA, used
// only inside that same detail page's "best matches" dock slot. Both
// variants are deliberately unaware of where they're placed — that's what
// lets DesignIdeaPageLayout drop either one into the exact same slot
// `BestMatchesHeader`/`DesignIdeaContractorRows` would otherwise occupy,
// with no structural difference between "city known" and "no city yet".
// Unlike the banner (which navigates to find-contractors on confirm — there's
// nowhere else on the landing page for matches to appear), compact-card's
// confirm must NOT redirect: this same slot already flips in place to real
// contractor rows once a city is set, so navigating away would just bounce
// the shopper off the page they're already on.
export default function BringIdeasToLife({ variant = "banner", trackingEvent = inhomzEvents.designIdeas.BROWSE_EXPERTS_CLICKED }) {
  const { openCityPopup } = useCityPopup();
  const track = useInhomzTrack();

  if (variant === "compact-card") {
    return (
      <div className="flex flex-col gap-3 rounded-[--radius16] bg-[--colorWarningWarning] p-8">
        <Image
          src="/assets/browse_prof_asset.webp"
          alt=""
          width={88}
          height={88}
          className="h-[88px] w-[88px] shrink-0 rounded-full bg-white object-cover"
        />

        <div className="flex flex-col gap-1">
          <h2 className="m3-headline-large text-[--textPrimaryPrimary]">Love an Idea? See It Built.</h2>
          <p className="m3-title-large text-[--textSecondarySecondary]">
            Connect with Interior Experts near you and bring these ideas to life.
          </p>
        </div>

        <div className="py-5 self-center whitespace-nowrap">
          <AppButton
            label="Browse Interior Experts"
            variant="contained"
            startIcon={<PersonSearchOutlinedIcon />}
            className="whitespace-nowrap"
            typographyClass="m3-label-large font-manrope"
            onClick={() => {
              track(trackingEvent, { placement: "compact_card" });
              openCityPopup({ redirectOnContinue: false });
            }}
            sx={BROWSE_BUTTON_SX}
          />
        </div>
      </div>
    );
  }

  return (
    <ProfessionalMatchBanner
      icon={
        <Image
          src="/assets/browse_prof_asset.webp"
          alt=""
          width={100}
          height={100}
          className="h-[60px] w-[60px] shrink-0 rounded-full bg-white object-cover md:h-[100px] md:w-[100px]"
        />
      }
      className="md:py-10"
      buttonClassName="self-center md:self-end"
      title="Love an Idea? See It Built."
      description="Connect with Interior Experts near you and bring these ideas to life."
      buttonLabel="Browse Interior Experts"
      buttonIcon={<PersonSearchOutlinedIcon />}
      onButtonClick={() => {
        track(trackingEvent, { placement: "banner" });
        openCityPopup();
      }}
      titleTypography="m3-title-large md:m3-headline-large"
      descriptionTypography="m3-body-medium text-[--textPrimaryPrimary] md:m3-title-large md:text-[--textSecondarySecondary]"
    />
  );
}
